import React, { useEffect, useState } from 'react';
import { Agent } from '../types/Agent';
import { SiteContent } from './admin/types';
import OptimizedImage from './OptimizedImage';

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
);

interface AgentDirectoryProps {
  onAgentClick: (agent: Agent) => void;
  onBioClick: (agent: Agent) => void;
}

const AgentDirectory: React.FC<AgentDirectoryProps> = ({ onAgentClick, onBioClick }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [content, setContent] = useState<SiteContent['agents']>({
    title: 'Meet Our Licensed Team',
    subtitle: 'Our dedicated Medicare specialists are here to help you navigate your healthcare options. Click on any team member to connect directly with them.',
    readyTitle: 'Ready to Get Started?',
    readySubtitle: 'Click on any team member above to schedule a free consultation or get in touch directly.',
    readyFooter: 'All consultations are completely free and there\'s no obligation to enroll.'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load agents from static JSON
        const agentsResponse = await fetch('/data/agents.json');
        if (agentsResponse.ok) {
          const agentsData = await agentsResponse.json();
          setAgents(agentsData);
        }

        // Load content from static JSON
        const contentResponse = await fetch('/data/content.json');
        if (contentResponse.ok) {
          const contentData = await contentResponse.json();
          if (contentData.agents) {
            setContent(contentData.agents);
          }
        }
      } catch (error) {
        console.error('Error loading static data:', error);
        // Fallback to empty arrays if static files not found
      }
    };

    loadData();
  }, []);



  return (
    <section id="agents" className="py-16 lg:py-24 bg-gradient-to-br from-bbha-cream-50 to-bbha-olive-100 relative overflow-hidden">

      
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10">
        {/* Header */}
        <div className="text-center space-y-4 lg:space-y-6 mb-12 lg:mb-16">
          <h2 id="agents-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-bbha-plum-900 leading-tight">
            {content.title}
          </h2>
          <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {content.subtitle}
          </p>
        </div>

        {/* Agent Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-8">
          {agents.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 mb-4">No agents found</div>
            </div>
          ) : agents.map((agent, index) => (
            <Card key={agent.id}
              className={`lg:col-span-2 ${
                agents.length % 3 === 1 && index === agents.length - 1 ? 'lg:col-start-3' :
                agents.length % 3 === 2 && index === agents.length - 2 ? 'lg:col-start-2' : ''
              }`}>
              <div
                onClick={() => onAgentClick(agent)}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer border border-gray-200"
              >
              {/* Agent Photo */}
              <div className="relative overflow-hidden group-hover:shadow-lg bg-gray-100 aspect-[5/4]">
                <OptimizedImage
                  src={agent.image}
                  alt={`Professional headshot of ${agent.name}, Independent Licensed Medicare Agent with license ${agent.licenseNumber}`}
                  className="h-full w-full transition-transform duration-300 group-hover:scale-105"
                  width={400}
                  height={320}
                  loading="lazy"
                  objectFit="cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
                {agent.agencyPartner && (
                  <div className="absolute inset-x-0 bottom-0 bg-bbha-plum-900/95 px-3 py-2 text-center text-sm font-semibold tracking-wide text-white shadow-lg backdrop-blur-sm sm:text-base">
                    Independent Agency Partner: <span className="font-extrabold">{agent.agencyPartner}</span>
                  </div>
                )}
              </div>

              {/* Agent Info */}
              <div className="p-3 lg:p-3">
                <h3 className="text-xl sm:text-2xl lg:text-2xl font-bold text-gray-900 mb-1 group-hover:text-bbha-plum-600 transition-colors duration-300">
                  {agent.name}
                </h3>
                {agent.licenseNumber && <p className="text-sm text-gray-500 mb-2">{agent.licenseNumber}</p>}
                <p className="text-bbha-plum-600 font-semibold mb-3 text-base sm:text-lg lg:text-lg">{agent.title}</p>

                {/* Connect CTA */}
                <div className="space-y-2">
                  <div className="bg-gradient-to-r from-bbha-olive-600 to-bbha-olive-700 text-white px-4 py-3 rounded-lg text-center font-semibold group-hover:from-bbha-olive-700 group-hover:to-bbha-olive-800 transition-all duration-300 text-base lg:text-lg shadow-lg group-hover:shadow-xl transform group-hover:scale-105">
                    Click to Connect
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!agent.bio && agent.websiteUrl && /^https?:\/\//.test(agent.websiteUrl)) {
                        window.open(agent.websiteUrl, '_blank', 'noopener,noreferrer');
                      } else if (!agent.bio && agent.planenrollUrl) {
                        window.open(agent.planenrollUrl, '_blank', 'noopener,noreferrer');
                      } else {
                        onBioClick(agent);
                      }
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 px-4 py-3 rounded-lg text-center font-medium transition-all duration-300 text-base lg:text-lg border border-gray-300 hover:border-gray-400"
                  >
                    Learn more about {agent.name.split(' ')[0]}
                  </button>
                </div>
              </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <Card>
          <div className="mt-12 lg:mt-16 text-center">
            <div className="bg-white rounded-xl p-8 lg:p-12 shadow-xl border-2">
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6">
                {content.readyTitle}
              </h3>
              <p className="text-lg lg:text-xl text-gray-600 mb-4 lg:mb-6 leading-relaxed">
                {content.readySubtitle}
              </p>
              <p className="text-base lg:text-lg text-gray-500">
                {content.readyFooter}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default AgentDirectory;
